"use client";

import { useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import KbNav from "@/src/components/common/knowledge-base/nav/kb-nav";
import LastUpdate from "@/src/components/common/pages/last-update/last-update";
import SocialIcons from "@/src/components/common/pages/social-icons/social-icons";
import Prose from "@/src/components/common/broadsheet/prose";
import { FormMasthead } from "@/src/components/common/broadsheet/form-kit";
import ReactHtmlParser from "@/src/utils/html-parser";
import {
  doubleRule,
  FONT_DISPLAY,
  FONT_TEXT,
  hairline,
  HEADING_SIZE,
  RAIL_LEFT,
  SHEET_GUTTER,
  STICKY_MAX_HEIGHT,
  STICKY_TOP,
} from "@/src/components/common/broadsheet/tokens";

export default function KnowledgeBase({ kbTree }: { kbTree: any }) {
  const [selectedSlug, setSelectedSlug] = useState("");
  const [kbContent, setKbContent] = useState<any>(null);
  const [loadingContent, setLoadingContent] = useState(false);

  useEffect(() => {
    if (selectedSlug) {
      const handleHashChange = async () => {
        setLoadingContent(true);
        try {
          const response = await fetch(`/api/kb?slug=${selectedSlug}`);
          if (!response.ok) throw new Error("Failed to fetch content");

          const body = await response.json();
          setKbContent(body.data);
        } catch (error) {
          console.error("Error fetching content:", error);
          setKbContent("<p>Failed to load content.</p>");
        }
        setLoadingContent(false);
      };
      handleHashChange();
    }
  }, [selectedSlug]);

  return (
    <Box className="kb-font" sx={{ px: SHEET_GUTTER, pb: { xs: 1.5, md: 2.5 } }}>
      <Box
        sx={{ pt: { xs: 1, md: 1.5 }, pb: { xs: 0.75, md: 1 }, ...doubleRule }}
      >
        <FormMasthead title="Knowledge Base">
          <SocialIcons direction="column" />
        </FormMasthead>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: `${RAIL_LEFT} 1fr` },
          columnGap: 2.5,
          rowGap: 1.25,
          alignItems: "start",
          pt: { xs: 1, md: 1.25 },
          "& > *": { minWidth: 0 },
        }}
      >
        <Box
          component="nav"
          aria-label="Knowledge base contents"
          sx={{
            position: { lg: "sticky" },
            top: { lg: STICKY_TOP },
            alignSelf: "start",
            maxHeight: { lg: STICKY_MAX_HEIGHT },
            overflowY: { lg: "auto" },
            overscrollBehavior: "contain",
            pr: { lg: 1 },
            borderRight: { lg: "1px solid" },
            borderColor: { lg: "custom.ruleHairline" },
          }}
        >
          <KbNav
            kbTree={kbTree}
            setSelectedSlug={setSelectedSlug}
            selectedSlug={selectedSlug}
          />
        </Box>

        <Box component="article">
          {loadingContent ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : kbContent ? (
            <>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "baseline",
                  justifyContent: "space-between",
                  columnGap: 1,
                  rowGap: 0.25,
                  pb: 0.5,
                  ...hairline,
                }}
              >
                {kbContent?.title ? (
                  <Typography
                    component="h2"
                    sx={{
                      m: 0,
                      fontFamily: FONT_DISPLAY,
                      fontWeight: 700,
                      fontSize: HEADING_SIZE.section,
                      lineHeight: 1.1,
                      letterSpacing: "0.01em",
                      color: "custom.ink",
                      maxWidth: "34ch",
                    }}
                  >
                    {kbContent.title}
                  </Typography>
                ) : null}
                {kbContent?.updated_at ? (
                  <LastUpdate updatedAt={kbContent.updated_at} />
                ) : null}
              </Box>
              <Prose sx={{ pt: 0.75 }}>{ReactHtmlParser(kbContent.body)}</Prose>
            </>
          ) : (
            <Box sx={{ py: { xs: 2, md: 4 } }}>
              <Typography
                component="p"
                sx={{
                  m: 0,
                  fontFamily: FONT_DISPLAY,
                  fontWeight: 700,
                  fontSize: HEADING_SIZE.section,
                  lineHeight: 1.15,
                  color: "custom.ink",
                }}
              >
                Guidance for authors, reviewers and editors
              </Typography>
              <Typography
                component="p"
                sx={{
                  m: 0,
                  mt: 0.375,
                  maxWidth: "58ch",
                  fontFamily: FONT_TEXT,
                  fontSize: "0.9375rem",
                  lineHeight: 1.6,
                  color: "custom.inkMuted",
                }}
              >
                Choose an entry from the contents to read it here, or filter the
                list to find a topic.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
